import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { SidebarPanel } from "../../components/SidebarPanel";
import { Button } from "../../components/Button";
import { formatDate, formatPeriod } from "../../utils/dateUtils";
import { formatMoney } from "../../utils/formatMoney";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import colors from "@styling/colors";
import { useMarkResellerInvoiceAsSentMutation } from "../../query/invoicesQuery";
import type { RestResellerInvoiceData } from "@KassaroAB/shared/types/invoices/resellerInvoice";
import type { ResellerId } from "@KassaroAB/shared/types/ids";
import { multiplyMoney } from "@KassaroAB/shared/utils/money";
import { kickbackSourcePeriod } from "@KassaroAB/shared/utils/kickbackPeriod";
import { KickbackSource } from "@KassaroAB/shared/types/enums/KickbackSource";
import { sumMoney } from "./kickback/kickbackTotals";
import { InfoBox } from "../../components/InfoBox";

const InvoiceTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${fontSizes.small}px;

  th {
    text-align: left;
    padding: ${spacing.x1}px 0;
    color: ${colors.textMuted};
    font-weight: 500;
    border-bottom: 1px solid ${colors.border};
  }

  td {
    padding: ${spacing.x1}px 0;
    border-bottom: 1px solid ${colors.border};
  }
`;

const InvoiceTotal = styled.div`
  font-size: ${fontSizes.large}px;
  font-weight: 600;
  color: ${colors.text};
`;

export const ResellerInvoiceResultPanel = ({
  invoice,
  resellerId,
  onClose,
  onInvoiceUpdated,
}: {
  invoice: RestResellerInvoiceData;
  resellerId?: ResellerId;
  onClose: () => void;
  onInvoiceUpdated?: (invoice: RestResellerInvoiceData) => void;
}) => {
  const { t } = useTranslation();
  const { mutate: markAsSent, isPending: isMarkingAsSent, error: markAsSentError } = useMarkResellerInvoiceAsSentMutation(resellerId);
  const isSent = invoice.sent ?? false;

  const handleMarkAsSent = () => {
    markAsSent(invoice.period, {
      onSuccess: (data) => onInvoiceUpdated?.(data),
    });
  };

  return (
    <SidebarPanel isOpen onClose={onClose} title={t("invoice.panelTitle")}>
      <div style={{ display: "flex", flexDirection: "column", gap: spacing.x3 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: spacing.x1 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: fontSizes.small, color: colors.textMuted }}>
            <span>{t("invoice.period")}</span>
            <span style={{ color: colors.text, fontWeight: 500 }}>
              {formatPeriod(invoice.period)}
            </span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: fontSizes.small, color: colors.textMuted }}>
            <span>{t("invoice.generatedAt")}</span>
            <span style={{ color: colors.text, fontWeight: 500 }}>{formatDate(invoice.generatedAt)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: fontSizes.small, color: colors.textMuted }}>
            <span>{t("invoice.sent")}</span>
            <span style={{ color: colors.text, fontWeight: 500 }}>{isSent ? t("common.yes") : t("common.no")}</span>
          </div>
        </div>
        {!isSent && (invoice.kickbackMissingSources ?? []).length > 0 && (
          <InfoBox variant="warning">
            {t("invoice.kickbackMissing", {
              sources: (invoice.kickbackMissingSources ?? [])
                .map((source) => t(`kickback.source.${source}`))
                .join(", "),
            })}
          </InfoBox>
        )}
        {resellerId && !isSent && (
          <Button
            onClick={handleMarkAsSent}
            disabled={isMarkingAsSent}
          >
            {isMarkingAsSent ? t("invoice.markingAsSent") : t("invoice.markAsSent")}
          </Button>
        )}
        {markAsSentError && (
          <div style={{ color: colors.danger }}>{markAsSentError.message}</div>
        )}
        {(invoice.lineItems ?? []).length + (invoice.kickbackLineItems ?? []).length > 0 ? (
          <>
            <InvoiceTable>
              <thead>
                <tr>
                  <th>{t("invoice.lineItem.store")}</th>
                  <th>{t("invoice.lineItem.name")}</th>
                  <th>{t("invoice.lineItem.type")}</th>
                  <th>{t("invoice.lineItem.period")}</th>
                  <th>{t("invoice.lineItem.months")}</th>
                  <th>{t("invoice.lineItem.unitPrice")}</th>
                  <th>{t("invoice.lineItem.total")}</th>
                </tr>
              </thead>
              <tbody>
                {Object.values(KickbackSource).map((source) => {
                  const amounts = (invoice.kickbackLineItems ?? [])
                    .filter((item) => item.source === source)
                    .map((item) => item.amount);
                  if (amounts.length === 0) {
                    return null;
                  }
                  const amount = sumMoney(amounts);
                  return (
                    <tr key={`kickback-${source}`}>
                      <td>–</td>
                      <td>{t("invoice.kickbackRow", { source: t(`kickback.source.${source}`) })}</td>
                      <td>{t("invoice.kickbackType")}</td>
                      <td>{formatPeriod(kickbackSourcePeriod(invoice.period, source))}</td>
                      <td>–</td>
                      <td>{formatMoney(multiplyMoney(amount, -1))}</td>
                      <td>{formatMoney(multiplyMoney(amount, -1))}</td>
                    </tr>
                  );
                })}
                {(invoice.lineItems ?? []).map((item, lineItemIndex) => (
                  <tr key={`${item.billingItemId}-${item.storeId}-${item.startDate}-${lineItemIndex}`}>
                    <td>{item.storeName}</td>
                    <td>{item.name}</td>
                    <td>{t(`billingItems.type.${item.type}`)}</td>
                    <td>{formatDate(item.startDate)} – {formatDate(item.endDate)}</td>
                    <td>{(Math.round(item.months * 1000) / 1000).toFixed(3).replace(/\.?0+$/, "")}</td>
                    <td>{formatMoney(item.unitPrice)}</td>
                    <td>{formatMoney(item.totalAmount)}</td>
                  </tr>
                ))}
              </tbody>
            </InvoiceTable>
            <InvoiceTotal>
              {t("invoice.total")}: {invoice.totalAmount ? formatMoney(invoice.totalAmount) : "–"}
            </InvoiceTotal>
          </>
        ) : (
          <p style={{ margin: 0, color: colors.textMuted }}>{t("invoice.empty")}</p>
        )}
      </div>
    </SidebarPanel>
  );
};
